import { useEffect, useMemo, useState } from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { pencilIcon } from '@progress/kendo-svg-icons';
import { Input, Checkbox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { DrawerItemProps } from '@progress/kendo-react-layout';
import { useLocalization } from '@progress/kendo-react-intl';
import { useToast } from '@chakra-ui/react';
import { enMessages } from '../../../../messages/en-US';
import {
  ActionItem,
  categorizeWorkOrdersActionForDetailTab,
  CauseCode,
  getActionKey,
  WorkOrdersActionDetailTab,
  WorkOrdersComplianceSettings,
  ScheduleClickPayload,
  WORK_ORDERS_LATE_PENDING_COLOR,
} from './WorkOrdersTypes';
import { WorkOrdersCauseCodeDialog } from './WorkOrdersCauseCodeDialog';
import { useCauseCodes } from './useCauseCodes';
import workOrdersReportApiService from '../../Utilities/WorkOrdersReportApiService';
import { GetHeadUnitIcon } from '../../../../Utilities/CustomIcons';

export type WorkOrdersDetailPanelItemProps = DrawerItemProps &
  ScheduleClickPayload & { onClose: () => void; compliance?: WorkOrdersComplianceSettings; timezone?: string };

const TAB_ORDER: WorkOrdersActionDetailTab[] = ['compliant', 'partial', 'nonCompliant', 'pending'];
const TAB_SORT_ORDER: WorkOrdersActionDetailTab[] = ['pending', 'nonCompliant', 'partial', 'compliant'];


function PointRow({
  action,
  causeCode,
  category,
  selected,
  onToggleSelect,
  onEditCauseCode,
  editLabel,
  greaseLabel,
  amountLabel,
  timeLabel,
  eventsLabel,
  volumeLabel,
  volReqLabel,
  toleranceLabel,
  occurredLabel,
  rfidLabel,
  headUnitLabel,
  compliance,
  timezone,
}: {
  action: ActionItem;
  causeCode?: CauseCode | null;
  category: WorkOrdersActionDetailTab;
  selected: boolean;
  onToggleSelect: () => void;
  onEditCauseCode: () => void;
  editLabel: string;
  greaseLabel: string;
  amountLabel: string;
  timeLabel: string;
  eventsLabel: string;
  volumeLabel: string;
  volReqLabel: string;
  toleranceLabel: string;
  occurredLabel: string;
  rfidLabel: string;
  headUnitLabel: string;
  compliance?: WorkOrdersComplianceSettings;
  timezone?: string;
}) {
  const isPending = category === 'pending';
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const events = action.events ?? [];

  return (
    <li className={`flex gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 ${selected ? 'bg-slate-50' : ''}`}>
      <span className="flex h-5 shrink-0 items-center">
        <Checkbox checked={selected} onChange={onToggleSelect} />
      </span>
      <div className="min-w-0 flex-1">
      <div className="text-sm font-medium break-words text-slate-800">{action.pointName}</div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span
          className={`inline-flex items-baseline gap-x-1 ${events.length > 0 ? 'cursor-pointer' : ''}`}
          onClick={() => events.length > 0 && setEventsExpanded((v) => !v)}
        >
          <span className="text-sm text-slate-400">{eventsLabel}</span>
          <span className="text-sm font-medium tabular-nums text-slate-700">{events.length || action.eventCount}</span>
          {events.length > 0 && (
            <span className="text-xs text-slate-400">{eventsExpanded ? '▲' : '▼'}</span>
          )}
        </span>
        <span className="text-slate-300" aria-hidden>·</span>
        {[
          { label: volumeLabel, value: `${Number(action.cumulativeVolumeDelivered).toFixed(2)} cc` },
          { label: volReqLabel, value: action.volumeRequirement === -1 ? 'Purge' : `${Number(action.volumeRequirement).toFixed(2)} cc` },
          { label: toleranceLabel, value: `${(Number(action.volumeTolerance) * 100).toFixed(0)}%` },
        ].map(({ label, value }, i, arr) => (
          <>
            <span key={label} className="inline-flex items-baseline gap-x-1">
              <span className="text-sm text-slate-400">{label}</span>
              <span className="text-sm font-medium tabular-nums text-slate-700">{value}</span>
            </span>
            {i < arr.length - 1 && <span key={`dot-${i}`} className="text-slate-300" aria-hidden>·</span>}
          </>
        ))}
      </div>
      {eventsExpanded && events.length > 0 && (
        <div className="mt-1.5 overflow-hidden rounded border border-slate-100 bg-slate-50">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-2 py-1 text-left font-medium text-slate-500">{occurredLabel}</th>
                <th className="px-2 py-1 text-left font-medium text-slate-500">{rfidLabel}</th>
                <th className="px-2 py-1 text-left font-medium text-slate-500">{volumeLabel} (cc)</th>
                <th className="px-2 py-1 text-left font-medium text-slate-500">{headUnitLabel}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 text-slate-600">
                    {ev.occurred
                      ? `${new Date(ev.occurred).toLocaleDateString(undefined, { timeZone: timezone })} ${new Date(ev.occurred).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: timezone })}`
                      : '—'}
                  </td>
                  <td className="px-2 py-1 font-mono text-slate-400">
                    {ev.rfid || '—'}
                  </td>
                  <td className="px-2 py-1 tabular-nums text-slate-600">
                    {(ev.volumeDelivered ?? ev.volumeDispensed) != null ? Number(ev.volumeDelivered ?? ev.volumeDispensed).toFixed(2) : '—'}
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    <span className="inline-flex items-center gap-x-1">
                      <span className="text-slate-400">{GetHeadUnitIcon(22, 22)}</span>
                      <span>{ev.deviceName?.trim() || '—'}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {compliance?.rightGrease !== false && (
          <Chip text={greaseLabel} themeColor={isPending ? 'base' : action.rightGrease ? 'success' : 'error'} style={{ pointerEvents: 'none' }} />
        )}
        {compliance?.rightAmount !== false && (
          <Chip text={amountLabel} themeColor={isPending ? 'base' : action.rightAmount ? 'success' : 'error'} style={{ pointerEvents: 'none' }} />
        )}
        {compliance?.rightTime !== false && (
          <Chip text={timeLabel} themeColor={isPending ? 'base' : action.rightTime ? 'success' : 'error'} style={{ pointerEvents: 'none' }} />
        )}
        {causeCode ? (
          <span className="ml-auto">
            <Tooltip anchorElement="target" position="top" parentTitle={true}>
              <span title={causeCode.description}>
                <Chip
                  text={causeCode.code}
                  themeColor="info"
                  style={{ cursor: 'pointer' }}
                  onClick={onEditCauseCode}
                />
              </span>
            </Tooltip>
          </span>
        ) : (
          <span className="ml-auto">
            <Button
              fillMode="flat"
              size="small"
              svgIcon={pencilIcon}
              title={editLabel}
              aria-label={editLabel}
              onClick={onEditCauseCode}
            />
          </span>
        )}
      </div>
      </div>
    </li>
  );
}

export function WorkOrdersDetailPanelItem(props: WorkOrdersDetailPanelItemProps) {
  const localizationService = useLocalization();
  // High z-index so the toast renders above the detail drawer overlay.
  const toast = useToast({ position: 'bottom', duration: 5000, isClosable: true, containerStyle: { zIndex: 99999 } });
  const causeCodes = useCauseCodes();
  const {
    onClose,
    assetId,
    scheduleName,
    assetName,
    windowStart,
    windowEnd,
    active,
    isOverdue,
    expected,
    actions,
    compliance,
    timezone,
  } = props;

  const [activeFilters, setActiveFilters] = useState<Set<WorkOrdersActionDetailTab>>(
    new Set(TAB_ORDER)
  );
  /** Late / on-time filter. Both on by default so the panel shows the whole work order. */
  const [timingFilters, setTimingFilters] = useState<Set<'onTime' | 'late'>>(
    new Set(['onTime', 'late'])
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [causeCodeDialogOpen, setCauseCodeDialogOpen] = useState(false);
  // Single-task edit target (chip / pencil). Bypasses `selectedKeys` so the row
  // doesn't show as checked and the bulk edit button stays hidden.
  const [singleEditKey, setSingleEditKey] = useState<string | null>(null);
  // Local cause-code overrides keyed by action key (until the backend persists them).
  // Stores the code string exactly as the backend exchanges it ('' = no cause code).
  const [causeCodeByKey, setCauseCodeByKey] = useState<Map<string, string>>(new Map());

  // Resolve a code string to its CauseCode (code + localized description) for display.
  const causeCodeByCode = useMemo(() => {
    const map = new Map<string, CauseCode>();
    causeCodes.forEach((c) => map.set(c.code, c));
    return map;
  }, [causeCodes]);

  useEffect(() => {
    setActiveFilters(new Set(TAB_ORDER));
    setTimingFilters(new Set(['onTime', 'late']));
    setSearchQuery('');
    setSelectedKeys(new Set());
    setCauseCodeDialogOpen(false);
    setSingleEditKey(null);
    setCauseCodeByKey(new Map());
  }, [actions]);

  // Stable key per action, computed against the original (unfiltered) array so
  // selection survives filtering/sorting. Swaps to backend taskId automatically.
  const keyOf = useMemo(() => {
    const map = new Map<ActionItem, string>();
    actions.forEach((a, i) => map.set(a, getActionKey(a, i)));
    return map;
  }, [actions]);

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applyCauseCode = async (causeCode: CauseCode | null, comment: string) => {
    const targetKeys = singleEditKey ? new Set([singleEditKey]) : selectedKeys;
    const selected = actions.filter((a) => targetKeys.has(keyOf.get(a)!));
    const code = causeCode?.code ?? '';
    const updates = selected.map((a) => ({ actionId: a.actionId, causeCode: code, comment }));

    // Snapshot for rollback, then apply optimistically and close.
    const prevByKey = new Map<string, string | undefined>();
    selected.forEach((a) => {
      const key = keyOf.get(a)!;
      prevByKey.set(key, causeCodeByKey.has(key) ? causeCodeByKey.get(key) : undefined);
    });
    setCauseCodeByKey((prev) => {
      const next = new Map(prev);
      selected.forEach((a) => next.set(keyOf.get(a)!, code));
      return next;
    });
    setCauseCodeDialogOpen(false);
    if (!singleEditKey) setSelectedKeys(new Set());
    setSingleEditKey(null);

    try {
      await workOrdersReportApiService.updateActionCauseCodes(updates);
      // Keep the in-memory gantt data in sync so reopening the drawer shows the new
      // value without re-fetching the report (actions are shared references).
      selected.forEach((a) => {
        a.causeCode = code;
        a.comment = comment;
      });
    } catch {
      // Roll back the optimistic update on failure.
      setCauseCodeByKey((prev) => {
        const next = new Map(prev);
        prevByKey.forEach((val, key) => (val === undefined ? next.delete(key) : next.set(key, val)));
        return next;
      });
      toast({
        title: localizationService.toLanguageString('custom.error', enMessages.custom.error),
        description: localizationService.toLanguageString(
          'custom.workOrdersCauseCodeSaveError',
          enMessages.custom.workOrdersCauseCodeSaveError
        ),
        status: 'error',
      });
    }
  };

  const causeCodeFor = (action: ActionItem): CauseCode | null => {
    const key = keyOf.get(action)!;
    const code = causeCodeByKey.has(key) ? causeCodeByKey.get(key)! : action.causeCode;
    if (!code) return null;
    return causeCodeByCode.get(code) ?? { code, description: code };
  };

  /**
   * Per-status counts for the compliance filter buttons, scoped to the active on-time/late filter:
   * both on → totals; only one → that timing's counts.
   */
  const statusCounts = useMemo(() => {
    const result: Record<WorkOrdersActionDetailTab, number> = {
      compliant: 0, partial: 0, nonCompliant: 0, pending: 0,
    };
    for (const a of actions) {
      const timing = a.isLate ? 'late' : 'onTime';
      if (!timingFilters.has(timing)) continue;
      result[categorizeWorkOrdersActionForDetailTab(a, compliance, active)]++;
    }
    return result;
  }, [actions, compliance, active, timingFilters]);

  const toggleFilter = (key: WorkOrdersActionDetailTab) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  /** Whether any task is late — drives showing the late / on-time filter at all. */
  const hasLate = useMemo(() => actions.some((a) => a.isLate), [actions]);

  const visibleActions = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return actions
      .filter((a) => {
        const tab = categorizeWorkOrdersActionForDetailTab(a, compliance, active);
        const timing = a.isLate ? 'late' : 'onTime';
        return activeFilters.has(tab) && timingFilters.has(timing) && a.pointName.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const tabA = TAB_SORT_ORDER.indexOf(categorizeWorkOrdersActionForDetailTab(a, compliance, active));
        const tabB = TAB_SORT_ORDER.indexOf(categorizeWorkOrdersActionForDetailTab(b, compliance, active));
        if (tabA !== tabB) return tabA - tabB;
        return a.pointName.localeCompare(b.pointName);
      });
  }, [actions, compliance, active, activeFilters, timingFilters, searchQuery]);

  const toggleTimingFilter = (key: 'onTime' | 'late') => {
    setTimingFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const actual = actions.length;

  // Keep the selection scoped to the filtered list: drop any selected keys that the
  // current filter/search hides, so "Select all", the count and bulk edit only ever
  // act on what's visible.
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const visibleKeys = new Set(visibleActions.map((a) => keyOf.get(a)!));
      const next = new Set([...prev].filter((k) => visibleKeys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleActions, keyOf]);

  const allVisibleSelected =
    visibleActions.length > 0 && visibleActions.every((a) => selectedKeys.has(keyOf.get(a)!));

  const toggleSelectAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleActions.forEach((a) => next.delete(keyOf.get(a)!));
      } else {
        visibleActions.forEach((a) => next.add(keyOf.get(a)!));
      }
      return next;
    });
  };

  const compliantL = localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant);
  const nonCompliantL = localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant);
  const pendingL = localizationService.toLanguageString('custom.pending', enMessages.custom.pending);
  const partialL = localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant);
  const onTimeFilterL = localizationService.toLanguageString('custom.workOrdersDetailOnTime', 'On-time');
  const lateFilterL = localizationService.toLanguageString('custom.workOrdersDetailLate', 'Late');
  const searchPlaceholderL = localizationService.toLanguageString(
    'custom.workOrdersDetailSearchPoints',
    enMessages.custom.workOrdersDetailSearchPoints
  );
  const noSearchResultsL = localizationService.toLanguageString(
    'custom.workOrdersDetailNoSearchResults',
    enMessages.custom.workOrdersDetailNoSearchResults
  );
  const greaseL = localizationService.toLanguageString('custom.rightGrease', enMessages.custom.rightGrease);
  const amountL = localizationService.toLanguageString('custom.rightAmount', enMessages.custom.rightAmount);
  const timeL = localizationService.toLanguageString('custom.rightTime', enMessages.custom.rightTime);
  const eventsL = localizationService.toLanguageString('custom.events', enMessages.custom.events);
  const volumeL = localizationService.toLanguageString('custom.volumeDelivered', enMessages.custom.volumeDelivered);
  const volReqL = localizationService.toLanguageString('custom.volumeRequirement', enMessages.custom.volumeRequirement);
  const toleranceL = localizationService.toLanguageString('custom.volumeTolerance', enMessages.custom.volumeTolerance);
  const occurredL = localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred);
  const rfidL = localizationService.toLanguageString('custom.rfid', enMessages.custom.rfid);
  const headUnitL = localizationService.toLanguageString('custom.headUnit', enMessages.custom.headUnit);
  const startL = localizationService.toLanguageString('custom.start', enMessages.custom.start);
  const endL = localizationService.toLanguageString('custom.end', enMessages.custom.end);
  const activeTagL = localizationService.toLanguageString(
    'custom.workOrdersDetailActiveTag',
    enMessages.custom.workOrdersDetailActiveTag
  );
  const expiredTagL = localizationService.toLanguageString(
    'custom.workOrdersDetailExpiredTag',
    enMessages.custom.workOrdersDetailExpiredTag
  );
  const lateTagL = localizationService.toLanguageString(
    'custom.workOrdersDetailLateTag',
    enMessages.custom.workOrdersDetailLateTag
  );
  const expectedMetaL = localizationService.toLanguageString(
    'custom.workOrdersDetailExpectedLabel',
    enMessages.custom.workOrdersDetailExpectedLabel
  );
  const actualMetaL = localizationService.toLanguageString(
    'custom.workOrdersDetailActualLabel',
    enMessages.custom.workOrdersDetailActualLabel
  );
  const selectAllL = localizationService.toLanguageString(
    'custom.workOrdersDetailSelectAll',
    enMessages.custom.workOrdersDetailSelectAll
  );
  const selectedL = localizationService.toLanguageString(
    'custom.workOrdersDetailSelected',
    enMessages.custom.workOrdersDetailSelected
  );
  const editCauseCodeL = localizationService.toLanguageString(
    'custom.workOrdersDetailEditCauseCode',
    enMessages.custom.workOrdersDetailEditCauseCode
  );

  // Schedule grouping merges assets — no single asset, so drop the asset line and lead with the schedule.
  const hasAsset = !!((assetName ?? '').trim() || (assetId ?? '').trim());
  const assetDisplay = (assetName ?? assetId ?? '').trim() || '—';
  const scheduleDisplay = scheduleName?.trim() || '—';
  const workOrderNumber = actions.find((a) => a.workOrderNumber?.trim())?.workOrderNumber?.trim() || undefined;

  const filterLabels: Record<WorkOrdersActionDetailTab, string> = {
    compliant: compliantL,
    nonCompliant: nonCompliantL,
    pending: pendingL,
    partial: partialL,
  };

  return (
    <div className="flex h-screen max-h-screen min-h-0 min-w-0 flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="shrink-0 px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 truncate text-base font-semibold leading-snug text-slate-900" title={hasAsset ? assetDisplay : scheduleDisplay}>
            {hasAsset ? assetDisplay : scheduleDisplay}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Chip
              text={active ? activeTagL : expiredTagL}
              themeColor={active ? 'success' : 'base'}
              className="!shrink-0"
            />
            {active && isOverdue && (
              <Chip
                text={lateTagL}
                className="!shrink-0"
                style={{
                  backgroundColor: WORK_ORDERS_LATE_PENDING_COLOR.fill,
                  borderColor: WORK_ORDERS_LATE_PENDING_COLOR.stroke,
                  color: WORK_ORDERS_LATE_PENDING_COLOR.stroke,
                }}
              />
            )}
            <Button fillMode="flat" themeColor="base" className="!shrink-0" onClick={onClose} aria-label="Close panel">
              ✕
            </Button>
          </div>
        </div>
        {hasAsset && (
          <div className="mt-1 truncate text-sm font-medium text-slate-800" title={scheduleDisplay}>
            {scheduleDisplay}
          </div>
        )}
        {workOrderNumber && (
          <div className="mt-0.5 inline-flex items-baseline gap-x-1">
            <span className="text-sm font-medium text-slate-700">{workOrderNumber}</span>
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-1">
          <div className="flex shrink-0 flex-col gap-0.5">
            <div className="text-[11px] uppercase leading-none tracking-wide text-slate-400">{startL}</div>
            <div className="text-sm font-medium leading-tight text-slate-900">{windowStart.toLocaleDateString(undefined, { timeZone: timezone })} {windowStart.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone })}</div>
          </div>
          <div className="flex shrink-0 flex-col gap-0.5">
            <div className="text-[11px] uppercase leading-none tracking-wide text-slate-400">{endL}</div>
            <div className="text-sm font-medium leading-tight text-slate-900">{windowEnd.toLocaleDateString(undefined, { timeZone: timezone })} {windowEnd.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone })}</div>
          </div>
          <div className="flex shrink-0 flex-col gap-0.5 border-l border-slate-200 pl-4">
            <div className="text-[11px] uppercase leading-none tracking-wide text-slate-400">{expectedMetaL}</div>
            <div className="text-sm font-medium leading-tight tabular-nums text-slate-900">{expected}</div>
          </div>
          <div className="flex shrink-0 flex-col gap-0.5">
            <div className="text-[11px] uppercase leading-none tracking-wide text-slate-400">{actualMetaL}</div>
            <div className="text-sm font-medium leading-tight tabular-nums text-slate-900">{actual}</div>
          </div>
        </div>

        {/* Timing (on-time / late) filter — above the compliance filters, same chip format, no counts */}
        {hasLate && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {([
              { key: 'onTime' as const, label: onTimeFilterL },
              { key: 'late' as const, label: lateFilterL },
            ]).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleTimingFilter(key)}
                className={`cursor-pointer rounded border bg-transparent px-2.5 py-1 text-sm font-medium focus:outline-none ${
                  timingFilters.has(key)
                    ? 'border-primary-500 text-primary-500'
                    : 'border-slate-300 text-slate-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Compliance status filters — counts follow the active on-time/late selection */}
        <div className={`${hasLate ? 'mt-2' : 'mt-3'} flex flex-wrap gap-1.5`}>
          {TAB_ORDER.filter((key) => statusCounts[key] > 0).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleFilter(key)}
              className={`cursor-pointer rounded border bg-transparent px-2.5 py-1 text-sm font-medium focus:outline-none ${
                activeFilters.has(key)
                  ? 'border-primary-500 text-primary-500'
                  : 'border-slate-300 text-slate-400'
              }`}
            >
              {`${filterLabels[key]} (${statusCounts[key]})`}
            </button>
          ))}
        </div>
      </div>

      {/* Search + list */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0 px-3 py-2">
          <div className="relative">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.value)}
              placeholder={searchPlaceholderL}
              className="w-full"
            />
            {searchQuery && (
              <Button
                fillMode="flat"
                themeColor="base"
                onClick={() => setSearchQuery('')}
                className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !p-0.5 !text-slate-400 hover:!text-slate-600"
                aria-label="Clear search"
              >
                ✕
              </Button>
            )}
          </div>
        </div>

        {visibleActions.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col justify-center px-4 py-6 text-center text-sm text-slate-500">
            {noSearchResultsL}
          </div>
        ) : (
          <>
          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-100 px-4 py-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <Checkbox checked={allVisibleSelected} onChange={toggleSelectAllVisible} />
              <span>
                {selectedKeys.size > 0 ? `${selectedKeys.size} ${selectedL}` : selectAllL}
              </span>
            </label>
            {selectedKeys.size > 0 && (
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                <Button
                  themeColor="primary"
                  svgIcon={pencilIcon}
                  title={editCauseCodeL}
                  aria-label={editCauseCodeL}
                  onClick={() => setCauseCodeDialogOpen(true)}
                />
              </div>
            )}
          </div>
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto [overflow-anchor:none]">
            {visibleActions.map((action, i) => (
              <PointRow
                key={keyOf.get(action) ?? `${action.pointName}-${i}`}
                action={action}
                causeCode={causeCodeFor(action)}
                selected={selectedKeys.has(keyOf.get(action)!)}
                onToggleSelect={() => toggleSelect(keyOf.get(action)!)}
                onEditCauseCode={() => {
                  setSingleEditKey(keyOf.get(action)!);
                  setCauseCodeDialogOpen(true);
                }}
                editLabel={editCauseCodeL}
                category={categorizeWorkOrdersActionForDetailTab(action, compliance, active)}
                greaseLabel={greaseL}
                amountLabel={amountL}
                timeLabel={timeL}
                eventsLabel={eventsL}
                volumeLabel={volumeL}
                volReqLabel={volReqL}
                toleranceLabel={toleranceL}
                occurredLabel={occurredL}
                rfidLabel={rfidL}
                headUnitLabel={headUnitL}
                compliance={compliance}
                timezone={timezone}
              />
            ))}
          </ul>
          </>
        )}
      </div>

      {causeCodeDialogOpen && (() => {
        const editKeys = singleEditKey ? new Set([singleEditKey]) : selectedKeys;
        const targetActions = actions.filter((a) => editKeys.has(keyOf.get(a)!));
        return (
          <WorkOrdersCauseCodeDialog
            selectedCount={editKeys.size}
            selectedName={editKeys.size === 1 ? targetActions[0]?.pointName : undefined}
            initialCauseCode={editKeys.size === 1 ? causeCodeFor(targetActions[0]) : undefined}
            initialComment={editKeys.size === 1 ? targetActions[0]?.comment ?? '' : undefined}
            causeCodes={causeCodes}
            onClose={() => {
              setCauseCodeDialogOpen(false);
              setSingleEditKey(null);
            }}
            onApply={applyCauseCode}
          />
        );
      })()}
    </div>
  );
}
